'use client'

import Image from 'next/image'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { MapPin, Plus, Check, Crown, Sparkles, ArrowRight } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useCart } from '@/lib/cart-store'
import type { Article } from '@/lib/site-config'
import { isArticlePrivelege } from '@/lib/site-config'

const BADGE_STYLES: Record<string, string> = {
  'Coup de cœur': 'bg-gold/15 text-gold border-gold/40',
  'Nouveau': 'bg-emerald/15 text-emerald border-emerald/40',
  'Édition limitée': 'bg-cuir/10 text-cuir border-cuir/30',
}

export function ArticleCard({ article }: { article: Article }) {
  const addItem = useCart(s => s.addItem)
  const [added, setAdded] = useState(false)

  // Image principale + variante pour hover (vraie image différente du même rayon)
  const total = 52
  const primaryIdx = article.imageIndex
  const variantIdx = (primaryIdx + 17) % total // +17 pour éviter la séquence de la fiche détail
  const imageUrl = `/images/products/${article.rayon}/${article.rayon}-${String(primaryIdx + 1).padStart(3, '0')}.png`
  const variantUrl = `/images/products/${article.rayon}/${article.rayon}-${String(variantIdx + 1).padStart(3, '0')}.png`
  const estPrivelege = isArticlePrivelege(article)

  const handleAdd = () => {
    addItem(article, 1)
    setAdded(true)
    setTimeout(() => setAdded(false), 1400)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`group relative flex flex-col bg-card rounded-xl border overflow-hidden transition-all ${
        estPrivelege
          ? 'border-gold/60 hover:border-gold shadow-luxe-deep hover:shadow-gold'
          : 'border-gold/20 hover:border-gold/50 shadow-luxury hover:shadow-gold'
      }`}
    >
      {/* Carte Privilège — coin haut gauche, en évidence */}
      {estPrivelege && (
        <div className="absolute top-0 left-0 z-20">
          <div className="relative">
            <div className="gradient-gold px-2.5 py-1 rounded-bl-lg flex items-center gap-1 shadow-luxury">
              <Crown className="h-3 w-3 text-cuir" />
              <span className="text-[9px] font-bold uppercase tracking-wider text-cuir">
                Privilège
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Image */}
      <Link href={`/article/${article.id}`} className="relative aspect-square overflow-hidden block">
        {/* Image variante — apparaît au survol (cross-fade) */}
        <Image
          src={variantUrl}
          alt={`${article.name} — vue alternative`}
          fill
          sizes="(max-width: 768px) 50vw, 25vw"
          className="object-cover absolute inset-0 opacity-0 scale-105 group-hover:opacity-100 group-hover:scale-100 transition-all duration-700 ease-out"
        />
        {/* Image principale */}
        <Image
          src={imageUrl}
          alt={article.name}
          fill
          sizes="(max-width: 768px) 50vw, 25vw"
          className="object-cover transition-transform duration-500 group-hover:scale-105 group-hover:opacity-0"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-cuir/60 via-transparent to-transparent" />

        {/* Indicateur de vues multiples — bas centre, n'apparaît qu'au survol */}
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 px-2.5 py-1 rounded-full bg-cuir/80 backdrop-blur border border-gold/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-150">
          <span className="h-1 w-1 rounded-full bg-gold" />
          <span className="h-1 w-1 rounded-full bg-gold/60" />
          <span className="h-1 w-1 rounded-full bg-gold/60" />
          <span className="h-1 w-1 rounded-full bg-gold/60" />
          <span className="ml-1 text-[8px] uppercase tracking-wider text-gold">4 vues</span>
        </div>

        {/* Lien "Voir la pièce" au survol */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          <span className="px-4 py-1.5 rounded-full bg-cuir/85 text-gold text-[10px] uppercase tracking-wider border border-gold/50 backdrop-blur flex items-center gap-1.5">
            Voir la pièce
            <ArrowRight className="h-3 w-3" />
          </span>
        </div>

        {/* Badge standard (Coup de cœur, Nouveau, Édition limitée) — décalé si Privilège */}
        {article.badge && (
          <div className={`absolute top-2 ${estPrivelege ? 'left-24' : 'left-2'}`}>
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${BADGE_STYLES[article.badge] ?? 'bg-card/80 text-cuir border-gold/30'} backdrop-blur`}>
              {article.badge}
            </Badge>
          </div>
        )}

        {/* Boutique Eva Diaw signature */}
        <div className="absolute top-2 right-2">
          <Badge variant="secondary" className="text-[9px] uppercase tracking-wider bg-cuir/85 text-gold border border-gold/40 backdrop-blur">
            Eva Diaw
          </Badge>
        </div>

        {/* Essence révélée au survol (pièces Privilège uniquement) */}
        {estPrivelege && article.essence && (
          <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-cuir/95 via-cuir/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
            <div className="flex items-start gap-1.5">
              <Sparkles className="h-3 w-3 text-gold flex-shrink-0 mt-0.5" />
              <p className="text-[10px] italic text-gold-soft leading-snug font-display line-clamp-3">
                {article.essence}
              </p>
            </div>
          </div>
        )}
      </Link>

      {/* Content */}
      <div className="flex flex-col flex-1 p-3">
        <Link href={`/article/${article.id}`}>
          <h4 className="font-display text-sm font-semibold text-cuir leading-tight line-clamp-2 min-h-[2.5rem] hover:text-gold transition-colors">
            {article.name}
          </h4>
        </Link>
        <div className="mt-1 flex items-center gap-1 text-[10px] text-foreground/60">
          <MapPin className="h-3 w-3 text-gold" />
          <span className="truncate">{article.origin}</span>
        </div>
        <p className="mt-1.5 text-[11px] text-foreground/70 line-clamp-2 leading-snug">
          {article.description}
        </p>

        {/* Footer */}
        <div className="mt-auto pt-3 flex items-end justify-between gap-2">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wider text-foreground/50">Prix</span>
            <span className="font-display text-lg font-bold text-cuir">
              {article.price.toFixed(2).replace('.', ',')} €
              {article.unit && (
                <span className="ml-1 text-[10px] font-normal text-foreground/60">{article.unit}</span>
              )}
            </span>
          </div>
          <Button
            size="sm"
            onClick={handleAdd}
            className={`h-8 px-3 transition-all ${added ? 'gradient-emerald-gold' : 'gradient-gold text-cuir hover:opacity-90'}`}
          >
            {added ? (
              <>
                <Check className="h-3.5 w-3.5 mr-1" />
                Ajouté
              </>
            ) : (
              <>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Ajouter
              </>
            )}
          </Button>
        </div>
      </div>
    </motion.div>
  )
}
